import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { ReactSVG } from "react-svg";
import NewToolTip from "../../../../common/New-ToolTip";
import { hasActiveInterventionLink } from "../../../Patient-Menu/Interventions/data";

const MedicationSidebarList = ({
  medications = [],
  title,
  isExpanded = true,
  onToggleExpanded,
  onMedicationClick,
  onExpandMedication,
  expandedMedications = [],
  hoveredMed,
  onMouseEnter,
  onMouseLeave,
  menuMedication,
  onMenuOpen,
  selectedMedication,
  isMatchingDrug,
  medicationDetails,
  drugType,
  isIntervention = false,
}) => {
  const [focusItem, setFocusItem] = useState(null);
  const [showTooltipFor, setShowTooltipFor] = useState(null);
  const tooltipTimeouts = useRef({});
  const expandedMedicationsRef = useRef(expandedMedications);

  useEffect(() => {
    expandedMedicationsRef.current = expandedMedications;
  }, [expandedMedications]);

  const interventions = useSelector((state) => state.intervention.interventions || []);
  const selectedMedicationId = useSelector((state) => state.medications.selectedMedicationId);
  const hasInterventionLink = (med) =>
    hasActiveInterventionLink(med, interventions);

  const handleMenuOpen = (event, med) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuData = {
      top: rect.top + window.scrollY,
      left: rect.right + 10,
    };
    onMenuOpen(event, med, menuData);
  };

  // Tooltip only for the truncated name area (not chevron / menu). Delayed to reduce noise while scrolling.
  const handleMedicationNameMouseEnter = (medId) => {
    if (expandedMedicationsRef.current.includes(medId)) return;
    clearTimeout(tooltipTimeouts.current[medId]);
    tooltipTimeouts.current[medId] = setTimeout(() => {
      if (expandedMedicationsRef.current.includes(medId)) return;
      setShowTooltipFor(medId);
    }, 700);
  };

  const handleMedicationNameMouseLeave = (medId) => {
    clearTimeout(tooltipTimeouts.current[medId]);
    delete tooltipTimeouts.current[medId];
    setShowTooltipFor((prev) => (prev === medId ? null : prev));
  };

  const handleMedicationNameDoubleClick = (medId) => {
    if (expandedMedicationsRef.current.includes(medId)) return;
    clearTimeout(tooltipTimeouts.current[medId]);
    delete tooltipTimeouts.current[medId];
    setShowTooltipFor(medId);
  };

  useEffect(() => () => {
    Object.values(tooltipTimeouts.current).forEach(clearTimeout);
  }, []);

  useEffect(() => {
    expandedMedications.forEach((id) => {
      if (tooltipTimeouts.current[id]) {
        clearTimeout(tooltipTimeouts.current[id]);
        delete tooltipTimeouts.current[id];
      }
    });
    setShowTooltipFor((prev) =>
      prev != null && expandedMedications.includes(prev) ? null : prev
    );
  }, [expandedMedications]);

  // Keep local focus highlight in sync with global selection, including explicit deselection when selectedMedicationId becomes null.
  useEffect(() => {
    setFocusItem(selectedMedicationId ?? null);
  }, [selectedMedicationId]);

  return (
    <div className="flex flex-col">
      <div
        className={`cursor-pointer rounded-[4px] flex justify-between h-[46px] pl-[28px] pr-[10px] border-[1px] border-gray-100 ${ isExpanded ? "border-b-0" : ""} ${ isExpanded ? "bg-gray-50" : "bg-gray-10" } items-center mt-2 flex-shrink-0`}
        onClick={onToggleExpanded}
      >
        <p className={`text-gray-800 text-base ${ isExpanded ? "font-semibold" : "" }`}>{title}</p>
        <ChevronDownIcon
          className={`w-4 h-4 ${isExpanded ? "-rotate-180" : ""}`}
        />
      </div>
      {isExpanded && (
        <>
          <ul
            className="divide-y divide-gray-100 border-y border-y-gray-100 overflow-y-auto pt-3 w-full max-h-[600px]"
            style={{ maxHeight: '600px' }}
            onMouseLeave={onMouseLeave} 
          >
            {medications.length > 0 ? (
              medications.map((med) => (
                (() => {
                  const isSelected =
                    expandedMedications.includes(med.id) ||
                    focusItem === med.id ||
                    (isMatchingDrug && isMatchingDrug(med)) ||
                    selectedMedication?.id === med.id;

                  return (
                <li
                  key={med.id}
                  className="cursor-pointer text-sm text-teal-600 w-full list-none"
                  onClick={(e) => {
                    setFocusItem(med.id);
                    onMedicationClick(med);
                    e.currentTarget?.scrollIntoView?.({
                      block: "nearest",
                      behavior: "smooth",
                    });
                  }}
                  onMouseEnter={() => {
                    onMouseEnter(med.id);
                  }}
                >
                  <>
                  <div
                    className={`w-full ${isSelected ? "bg-blue-100 hover:bg-blue-100" : "hover:bg-gray-10"}`}
                  >
                    <div
                      className={`pr-[10px] flex w-full items-center py-[10px] ${
                        med.status === "inactive" && "bg-red-50"
                      }`}
                    >
                      {/* Tooltip anchor: full row body except expand/collapse chevron */}
                      <div
                        data-medication-tooltip={`${drugType || "default"}-${med.id}`}
                        className="flex min-w-0 flex-1 items-center gap-3 pr-3"
                        onMouseEnter={() => handleMedicationNameMouseEnter(med.id)}
                        onMouseLeave={() => handleMedicationNameMouseLeave(med.id)}
                        onDoubleClick={() => handleMedicationNameDoubleClick(med.id)}
                      >
                        <div className="min-w-0 flex-1 overflow-hidden text-left">
                          <span className="flex min-w-0 items-center gap-[10px] px-[2px]">
                            <span className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
                              {isIntervention && hasInterventionLink(med) ? (
                                <ReactSVG
                                  src="/icons/intervention-sm.svg"
                                  className="h-4 w-4"
                                />
                              ) : null}
                            </span>
                            <span className="block truncate text-left">
                              {med.displayName}
                            </span>
                          </span>
                        </div>
                        {(hoveredMed === med.id || menuMedication?.id === med.id) && (
                          <Menu as="div" className="relative flex-shrink-0">
                            <Menu.Button
                              onClick={(e) => {
                                setFocusItem(med.id);
                                handleMenuOpen(e, med);
                              }}
                            >
                              <ReactSVG
                                src={`/icons/triple-dot.svg`}
                                className={`flex !items-center rounded-md px-3 py-2 ${
                                  med.id === menuMedication?.id && "bg-gray-300"
                                }`}
                              />
                            </Menu.Button>
                          </Menu>
                        )}
                      </div>
                      <button
                        type="button"
                        className="flex-shrink-0"
                        onClick={() => {
                          onExpandMedication(med.id);
                        }}
                      >
                        {!expandedMedications.includes(med.id) && (
                          <ChevronDownIcon className="h-4 w-4" strokeWidth={2} />
                        )}
                        {expandedMedications.includes(med.id) && (
                          <ChevronUpIcon className="h-4 w-4" strokeWidth={2} />
                        )}
                      </button>
                    </div>
                  </div>
                  {expandedMedications.includes(med.id) && medicationDetails(med)}
                  <NewToolTip
                    id={`medication-sidebar-name-${drugType || "default"}-${med.id}`}
                    anchorSelect={`[data-medication-tooltip="${drugType || "default"}-${med.id}"]`}
                    place="bottom-start"
                    isOpen={
                      showTooltipFor === med.id &&
                      !expandedMedications.includes(med.id)
                    }
                    render={() => (
                      <div className="bg-teal-25 p-2 max-w-md flex flex-col">
                        <p className="text-teal-300 text-left text-body-sm font-normal">
                          {med.displayName}
                        </p>
                        {(med?.manufacturerStoredType === "Small" && med?.manufacturerCode) ||
                        (med?.manufacturerStoredType === "Full" && med?.manufacturerName) ? (
                          <p className="text-teal-300 text-left text-body-sm font-normal">
                            {med?.manufacturerStoredType === "Small" && med?.manufacturerCode
                              ? ` (${med?.manufacturerCode})`
                              : ` (${med?.manufacturerName})`}
                          </p>
                        ) : null}
                      </div>
                    )}
                  />
                  </>
                </li>
                  );
                })()
              ))
            ) : (
              <li className="pl-7">
                <div className="flex items-center justify-start h-[50px] text-sm font-normal">
                  <span>No Records Found</span>
                </div>
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  );
};

export default MedicationSidebarList;
